import React, { useState } from 'react';
import { InternalLinkGapResult, PageGapAnalysis, PotentialAnchor } from '../services/internalLinkGap';
import { User } from '../types';

interface InternalLinkGapPanelProps {
  gapResult: InternalLinkGapResult | null;
  isLoading?: boolean;
  user: User;
  onAddTodo?: (todoData: any) => Promise<any>;
  addedTodos?: Set<string>;
}

// Gradient SVG Icons Component
const GradientIcon: React.FC<{ type: string; size?: string }> = ({ type, size = "w-5 h-5" }) => {
  const iconPaths: { [key: string]: string } = {
    link: "M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244",
    anchor: "M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0118 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25",
    target: "M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z",
    priority: "M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z",
    expand: "M19 9l-7 7-7-7",
    collapse: "M5 15l7-7 7 7",
    download: "M12 15.75l-4.5-4.5h3v-9h3v9h3l-4.5 4.5zM3 19.5h18v1.5H3v-1.5z",
    blog: "M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
  };

  return (
    <svg className={size} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id={`gradient-${type}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#059669" />
          <stop offset="100%" stopColor="#0d9488" />
        </linearGradient>
      </defs>
      <path
        d={iconPaths[type] || iconPaths.link}
        stroke={`url(#gradient-${type})`}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Priority badge component
const PriorityBadge: React.FC<{ priority: 'high' | 'medium' | 'low' }> = ({ priority }) => {
  const styles = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-gray-100 text-gray-600 border-gray-200'
  };

  const labels = {
    high: 'High Priority',
    medium: 'Medium Priority', 
    low: 'Low Priority'
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${styles[priority]}`}>
      {labels[priority]}
    </span>
  );
};

// Page analysis card component
const PageAnalysisCard: React.FC<{
  pageAnalysis: PageGapAnalysis;
  onAddTodo?: (todoData: any) => Promise<any>;
  addedTodos?: Set<string>;
}> = ({ pageAnalysis, onAddTodo, addedTodos = new Set() }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedPriorities, setSelectedPriorities] = useState<Array<'high' | 'medium' | 'low'>>(['high', 'medium', 'low']);
  const [generatedTitles, setGeneratedTitles] = useState<{[keyword: string]: string[]}>({});
  const [isGeneratingTitles, setIsGeneratingTitles] = useState<{[keyword: string]: boolean}>({});
  const [showTitles, setShowTitles] = useState<{[keyword: string]: boolean}>({});

  const filteredAnchors = pageAnalysis.potentialAnchors.filter(anchor =>
    selectedPriorities.includes(anchor.priority)
  );

  const handlePriorityToggle = (priority: 'high' | 'medium' | 'low') => {
    setSelectedPriorities(prev =>
      prev.includes(priority)
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    );
  };

  const generateBlogTitles = async (anchor: PotentialAnchor) => {
    setIsGeneratingTitles(prev => ({...prev, [anchor.keyword]: true}));
    try {
      const response = await fetch('/api/generate-blog-titles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pageData: pageAnalysis,
          anchorData: [anchor] // Send just this specific anchor
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setGeneratedTitles(prev => ({...prev, [anchor.keyword]: result.titles || []}));
      setShowTitles(prev => ({...prev, [anchor.keyword]: true}));
    } catch (error) {
      console.error('Error generating blog titles:', error);
      alert(`Failed to generate blog titles for "${anchor.keyword}". Please try again.`);
    } finally {
      setIsGeneratingTitles(prev => ({...prev, [anchor.keyword]: false}));
    }
  };

  const downloadBlogTitles = (anchor: PotentialAnchor) => {
    const titles = generatedTitles[anchor.keyword];
    if (!titles || titles.length === 0) return;

    const content = [
      `Blog Title Suggestions for: ${pageAnalysis.path}`,
      `Target URL: ${pageAnalysis.targetUrl}`,
      `Keyword: "${anchor.keyword}"`,
      `Generated: ${new Date().toLocaleDateString()}`,
      '',
      'Blog Titles:',
      ...titles.map((title, index) => `${index + 1}. "${title}"`),
      '',
      'Anchor Text Details:',
      `- "${anchor.keyword}" (${anchor.clicks} clicks, pos ${anchor.position.toFixed(1)}, ${anchor.priority} priority)`,
      `- Reason: ${anchor.reason}`,
      '',
      '💡 These titles are designed to naturally link back to the target page using this specific anchor text'
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `blog-titles-${anchor.keyword.replace(/[^a-zA-Z0-9]/g, '-')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleAddTodo = async (anchor: PotentialAnchor) => {
    if (typeof onAddTodo !== 'function') return;

    try {
      await onAddTodo({
        title: `Optimize "${anchor.keyword}" for ${pageAnalysis.path}`,
        description: `${anchor.reason} - ${anchor.clicks} clicks, position ${anchor.position.toFixed(1)}`,
        priority: anchor.priority,
        sourcePanel: 'Internal Link Gap'
      });
    } catch (error) {
      console.error('Error adding to-do:', error);
      alert('Failed to add to-do item');
    }
  };

  const getTodoId = (anchor: PotentialAnchor) => {
    return `linkgap-${pageAnalysis.targetUrl}-${anchor.keyword}`;
  };

  const getTodoStatus = (anchor: PotentialAnchor) => {
    const todoId = getTodoId(anchor);
    if (addedTodos.has(todoId)) {
      return 'added'; // Could be expanded to track actual status
    }
    return 'not-added';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4">
        {/* Page Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <GradientIcon type="target" size="w-4 h-4" />
              <h3 className="text-sm font-medium text-gray-600">{pageAnalysis.domain}</h3>
            </div>
            <a 
              href={pageAnalysis.targetUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-lg font-semibold text-blue-600 hover:text-blue-800 truncate block transition-colors"
              title={`Open ${pageAnalysis.targetUrl} in new tab`}
            >
              {pageAnalysis.path}
            </a>
            <p className="text-sm text-gray-600 mt-1">
              {pageAnalysis.totalKeywords} keywords • {pageAnalysis.potentialAnchors.length} anchor opportunities
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs bg-green-100 text-green-600 px-3 py-1 rounded hover:bg-green-200 transition-colors flex items-center gap-1"
            >
              <GradientIcon type={isExpanded ? "collapse" : "expand"} size="w-3 h-3" />
              {isExpanded ? 'Less' : 'View Anchors'}
            </button>
          </div>
        </div>

        {/* Page Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-lg font-bold text-blue-600">{pageAnalysis.totalClicks.toLocaleString()}</div>
            <div className="text-xs text-blue-800">Total Clicks</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <div className="text-lg font-bold text-green-600">{pageAnalysis.totalImpressions.toLocaleString()}</div>
            <div className="text-xs text-green-800">Impressions</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-3">
            <div className="text-lg font-bold text-purple-600">{pageAnalysis.averagePosition.toFixed(1)}</div>
            <div className="text-xs text-purple-800">Avg Position</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-3">
            <div className="text-lg font-bold text-orange-600">{pageAnalysis.potentialAnchors.length}</div>
            <div className="text-xs text-orange-800">Opportunities</div>
          </div>
        </div>


        {/* Expanded Anchor Opportunities */}
        {isExpanded && (
          <div>
            {/* Priority Filters */}
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">Show:</span>
              {(['high', 'medium', 'low'] as const).map(priority => (
                <button
                  key={priority}
                  onClick={() => handlePriorityToggle(priority)}
                  className={`text-xs px-2 py-1 rounded border transition-colors ${
                    selectedPriorities.includes(priority)
                      ? 'bg-green-100 text-green-700 border-green-200'
                      : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {priority} ({pageAnalysis.potentialAnchors.filter(a => a.priority === priority).length})
                </button>
              ))}
            </div>

            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1">
              <GradientIcon type="anchor" size="w-4 h-4" />
              Potential Anchor Texts ({filteredAnchors.length} shown)
            </h4>
            
            <div className="max-h-96 overflow-y-auto space-y-3">
              {filteredAnchors.slice(0, 20).map((anchor, index) => (
                <div key={index} className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-3 border border-green-200">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 mb-1" title={anchor.keyword}>
                        "{anchor.keyword}"
                      </div>
                      <div className="flex items-center gap-2">
                        <PriorityBadge priority={anchor.priority} />
                        <button
                          onClick={() => generateBlogTitles(anchor)}
                          disabled={isGeneratingTitles[anchor.keyword]}
                          className={`text-xs px-2 py-1 rounded transition-colors flex items-center gap-1 ${
                            isGeneratingTitles[anchor.keyword]
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                          }`}
                          title={`Generate blog titles for "${anchor.keyword}"`}
                        >
                          <GradientIcon type="blog" size="w-3 h-3" />
                          {isGeneratingTitles[anchor.keyword] ? 'Generating...' : 'Blog Ideas'}
                        </button>
                        {onAddTodo && (
                          getTodoStatus(anchor) === 'not-added' ? (
                            <button
                              onClick={() => handleAddTodo(anchor)}
                              className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded transition-colors"
                              title="Add this optimization task to your to-do list"
                            >
                              + To-Do
                            </button>
                          ) : (
                            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">
                              ✓ Added
                            </span>
                          )
                        )}
                      </div>
                    </div>
                    <div className="flex gap-3 text-right text-sm">
                      <div>
                        <div className="font-medium text-blue-600">{anchor.clicks}</div>
                        <div className="text-gray-500">clicks</div>
                      </div>
                      <div>
                        <div className="font-medium text-green-600">{anchor.impressions}</div>
                        <div className="text-gray-500">impr</div>
                      </div>
                      <div>
                        <div className="font-medium text-purple-600">{anchor.position.toFixed(1)}</div>
                        <div className="text-gray-500">pos</div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 italic">{anchor.reason}</p>
                  
                  {/* Generated Blog Titles for this anchor */}
                  {showTitles[anchor.keyword] && generatedTitles[anchor.keyword] && generatedTitles[anchor.keyword].length > 0 && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-xs font-medium text-blue-900 flex items-center gap-1">
                          <GradientIcon type="blog" size="w-3 h-3" />
                          Blog Ideas for "{anchor.keyword}" ({generatedTitles[anchor.keyword].length})
                        </h5>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => downloadBlogTitles(anchor)}
                            className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded hover:bg-blue-200 transition-colors flex items-center gap-1"
                            title="Download blog titles as text file"
                          >
                            <GradientIcon type="download" size="w-2 h-2" />
                            Download
                          </button>
                          <button
                            onClick={() => setShowTitles(prev => ({...prev, [anchor.keyword]: false}))}
                            className="text-blue-600 hover:text-blue-800 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mb-3 italic">
                        These are guidelines only you should research these titles before writting and publishing on your site.
                      </p>
                      <div className="space-y-2">
                        {generatedTitles[anchor.keyword].map((title, titleIndex) => (
                          <div key={titleIndex} className="bg-white rounded p-2 border border-blue-100 hover:border-blue-200 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-800 font-medium italic">
                                  "{title}"
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Uses "{anchor.keyword}" • Target: {pageAnalysis.path}
                                </p>
                              </div>
                              {typeof onAddTodo === 'function' && (
                                <button
                                  onClick={() => onAddTodo({
                                    title: `Write blog: ${title}`,
                                    priority: 'medium' as const,
                                    sourcePanel: 'Content Suggestions'
                                  })}
                                  className="text-xs bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 text-white px-2 py-1 rounded transition-colors ml-2"
                                  title="Add this blog writing task to your to-do list"
                                >
                                  + To-Do
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-blue-600">
                        💡 These titles are designed to naturally link back to "{pageAnalysis.path}" using "{anchor.keyword}"
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {filteredAnchors.length > 20 && (
                <div className="text-xs text-gray-500 text-center py-2">
                  ... and {filteredAnchors.length - 20} more anchor opportunities
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InternalLinkGapPanel: React.FC<InternalLinkGapPanelProps> = ({
  gapResult,
  isLoading = false,
  user,
  onAddTodo,
  addedTodos = new Set()
}) => {
  const [priorityFilter, setPriorityFilter] = useState<Array<'high' | 'medium' | 'low'>>(['high', 'medium', 'low']);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // CSV Download function
  const downloadCSV = () => {
    if (!gapResult) return;

    // Check if user is on free plan (admins bypass this restriction)
    if (user.plan === 'free' && !user.isAdmin) {
      setShowUpgradeModal(true);
      return;
    }

    const csvData = [];
    // CSV Headers
    csvData.push([
      'Page URL',
      'Domain', 
      'Page Path',
      'Total Clicks',
      'Total Impressions',
      'Average Position',
      'Keywords Count',
      'Anchor Keyword',
      'Anchor Clicks',
      'Anchor Impressions', 
      'Anchor Position',
      'Priority',
      'Reason'
    ]);

    // Add data for each page and its anchors
    gapResult.pageAnalyses.forEach(page => {
      if (page.potentialAnchors.length === 0) {
        // Page with no anchors
        csvData.push([
          page.targetUrl,
          page.domain,
          page.path,
          page.totalClicks,
          page.totalImpressions,
          page.averagePosition.toFixed(2),
          page.totalKeywords,
          '', // No anchor data
          '',
          '',
          '',
          '',
          'No anchor opportunities found'
        ]);
      } else {
        // Page with anchors
        page.potentialAnchors.forEach(anchor => {
          csvData.push([
            page.targetUrl,
            page.domain,
            page.path,
            page.totalClicks,
            page.totalImpressions,
            page.averagePosition.toFixed(2),
            page.totalKeywords,
            anchor.keyword,
            anchor.clicks,
            anchor.impressions,
            anchor.position.toFixed(2),
            anchor.priority,
            anchor.reason
          ]);
        });
      }
    });

    // Convert to CSV string
    const csvString = csvData.map(row => 
      row.map(cell => 
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(',')
    ).join('\n');

    // Download the CSV
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `internal-link-gap-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!gapResult) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-gray-200 p-6 mb-6">
        <div className="text-center">
          <div className="mb-2 inline-block">
            <GradientIcon type="link" size="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mt-2">Internal Link Gap Analysis</h2>
          <p className="text-gray-600 mt-1">Load your SEO data to discover internal linking opportunities and potential anchor texts.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-gray-200 p-6 mb-6">
        <div className="text-center">
          <div className="mb-2 inline-block animate-spin">
            <GradientIcon type="link" size="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mt-2">Analyzing Internal Link Gaps</h2>
          <p className="text-gray-600 mt-1">Identifying anchor text opportunities for each target URL...</p>
        </div>
      </div>
    );
  }

  const filteredPages = gapResult.pageAnalyses.filter(page =>
    page.potentialAnchors.some(anchor => priorityFilter.includes(anchor.priority))
  );

  const handlePriorityFilterToggle = (priority: 'high' | 'medium' | 'low') => {
    setPriorityFilter(prev =>
      prev.includes(priority)
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    );
  };

  return (
    <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-gray-200 p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <GradientIcon type="link" size="w-6 h-6" />
            Internal Link Gap Analysis
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Found {gapResult.totalPotentialAnchors} anchor text opportunities across {gapResult.totalPages} pages
          </p>
        </div>
        <button
          onClick={downloadCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
          title="Download Internal Link Gap Analysis as CSV"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download CSV
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-red-600">{gapResult.summary.highPriorityOpportunities}</div>
          <div className="text-sm text-gray-600">High Priority</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-yellow-600">{gapResult.summary.mediumPriorityOpportunities}</div>
          <div className="text-sm text-gray-600">Medium Priority</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-600">{gapResult.summary.lowPriorityOpportunities}</div>
          <div className="text-sm text-gray-600">Low Priority</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-green-600">{gapResult.totalPages}</div>
          <div className="text-sm text-gray-600">Target Pages</div>
        </div>
      </div>

      {/* Priority Filter */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm font-medium text-gray-700">Show priorities:</span>
        {(['high', 'medium', 'low'] as const).map(priority => (
          <button
            key={priority}
            onClick={() => handlePriorityFilterToggle(priority)}
            className={`text-xs px-3 py-1 rounded border transition-colors ${
              priorityFilter.includes(priority)
                ? 'bg-green-100 text-green-700 border-green-200'
                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
            }`}
          >
            {priority} ({
              priority === 'high' ? gapResult.summary.highPriorityOpportunities :
              priority === 'medium' ? gapResult.summary.mediumPriorityOpportunities :
              gapResult.summary.lowPriorityOpportunities
            })
          </button>
        ))}
      </div>

      {/* Page Analysis Cards */}
      <div className="space-y-4">
        {filteredPages.slice(0, 10).map((pageAnalysis) => (
          <PageAnalysisCard
            key={pageAnalysis.targetUrl}
            pageAnalysis={pageAnalysis}
            onAddTodo={onAddTodo}
            addedTodos={addedTodos}
          />
        ))}
        
        {filteredPages.length > 10 && (
          <div className="text-center py-4 text-gray-600">
            <p>Showing top 10 pages. {filteredPages.length - 10} more pages available.</p>
          </div>
        )}
        
        {filteredPages.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-600">No pages found with the selected priority filters.</p>
          </div>
        )}
      </div>

      {/* Footer Instructions */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="bg-white/60 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-800 mb-2">💡 How to use this analysis:</h4>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• <strong>High Priority</strong>: Keywords with good traffic or high visibility that could benefit most from internal links</li>
            <li>• <strong>Medium Priority</strong>: Keywords with some performance that could be boosted with better internal linking</li>
            <li>• <strong>Low Priority</strong>: Additional anchor text opportunities for comprehensive internal linking</li>
            <li>• Consider creating internal links from topically related pages using the suggested anchor texts</li>
          </ul>
        </div>
      </div>

      {/* Upgrade Modal for CSV Export */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Upgrade to Pro Plan</h3>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                CSV exports are available on the Pro plan. Export your data to Excel or Google Sheets for deeper analysis and reporting.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Pro Plan includes:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    CSV exports for all data
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Unlimited GSC properties
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    16 months of historical data
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    AI-powered content briefs
                  </li>
                </ul>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">£15/month</p>
              <p className="text-sm text-gray-600">Including VAT</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Maybe Later
              </button>
              <a
                href="https://buy.stripe.com/5kA9CV7tF7iw8IEcMP"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-center font-medium"
              >
                Upgrade Now
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalLinkGapPanel;